/**
 * Manages custom navbar behaviors:
 * 1. Shows transparent/opaque background on page load; fades it out as soon as scrolling begins.
 * 2. Swaps the logo (white ↔ black) based on the data-nav-theme of the
 *    section currently overlapping the navbar centre point.
 *
 * Works with Lenis smooth scrolling when window.lenis is available.
 */
const initNavbar = () => {
  const navbar = document.querySelector('.custom-navbar');
  if (!navbar) return;

  const sections = document.querySelectorAll('[data-nav-theme]');

  // Helper: get the mid-point of the navbar where theme is sampled
  const getTriggerPoint = () => (navbar.offsetHeight || 70) / 2;

  // 1. Background fade-out on scroll
  const updateBackground = (scrollY) => {
    if (scrollY > 10) {
      navbar.classList.add('scrolled-transparent');
    } else {
      navbar.classList.remove('scrolled-transparent');
    }
  };

  // 2. Theme detection: which section sits behind the navbar?
  const updateTheme = (scrollY) => {
    const triggerPoint = getTriggerPoint();
    let activeTheme = 'dark'; // default: assume dark

    sections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      const style = window.getComputedStyle(section);
      const marginTop = parseFloat(style.marginTop) || 0;
      const sectionStart = rect.top - marginTop;

      // If the start of the section (including its margin-top) has crossed the trigger point
      if (sectionStart <= triggerPoint) {
        const theme = section.getAttribute('data-nav-theme');
        if (theme) activeTheme = theme;
      }
    });

    if (activeTheme === 'light') {
      navbar.classList.add('theme-light');
      navbar.classList.remove('theme-dark');
    } else {
      navbar.classList.add('theme-dark');
      navbar.classList.remove('theme-light');
    }
  };

  // Combined handler
  let lastScrollY = 0;
  const threshold = 12; // Min scroll offset delta before updating visibility
  
  const handleScroll = ({ scroll } = {}) => {
    const scrollY = scroll !== undefined ? scroll : (window.scrollY || window.pageYOffset);
    updateBackground(scrollY);
    updateTheme(scrollY);

    // Bounce scroll protection (e.g. Safari elastic scroll-up past 0)
    if (scrollY < 0) {
      navbar.classList.remove('navbar-hidden');
      return;
    }

    // Always show navbar near the top of the page
    if (scrollY <= 50) {
      navbar.classList.remove('navbar-hidden');
      lastScrollY = scrollY;
      return;
    }

    const diff = scrollY - lastScrollY;

    // Only update visibility if scroll change exceeds threshold (prevents micro-stutter/jitter)
    if (Math.abs(diff) > threshold) {
      if (diff > 0 && scrollY > 100) {
        navbar.classList.add('navbar-hidden');
      } else {
        navbar.classList.remove('navbar-hidden');
      }
      lastScrollY = scrollY;
    }
  };

  // Native scroll fallback
  window.addEventListener('scroll', handleScroll, { passive: true });

  // Lenis integration
  if (window.lenis) {
    window.lenis.on('scroll', handleScroll);
  }

  window.addEventListener('resize', handleScroll);

  // Run immediately on load
  handleScroll();
};

export default initNavbar;
